import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from 'react-apollo';
import { makeStyles, Drawer } from '@material-ui/core';
import Routes from 'utils/routes/appRoutes';
import { withRouter } from 'react-router-dom';
import { compose, mapProps } from 'recompose';
import { listAssessments, getDomainRestrictedTemplates } from 'graphql/operations';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import { getAssessments } from 'selectors/assessments/selector';
import { getDomainRestrictedTemplatesSelector } from 'selectors/templates/selector';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';
import { SHOW_HOME_GUIDED_TOUR_KEY } from 'components/threepp/home/HomeGuidedTour/HomeGuidedTour';
import { NavLinkSection } from './NavLinkSection';

const useStyles = makeStyles(() => ({
  drawerRoot: {
    zIndex: '1200!important'
  },
  drawerPaper: {
    backgroundColor: '#000000',
    color: '#ffffff',
    marginTop: '3.1rem',
    minWidth: '290px',
    maxWidth: '310px',
    padding: '0 1rem 1rem'
  },
  drawerDocked: {
    height: '100%'
  }
}));

export const NavSideBarBase = ({
  open,
  navDrawerOnChange,
  toggleUserProfileModal,
  displayHelpModal,
  displayCreateNewModal,
  history,
  assessments,
  templateData
}) => {
  const { t: translate } = useTranslation();
  const { drawerRoot, drawerPaper, drawerDocked } = useStyles();
  const isHomePage = window.location?.pathname?.indexOf('assessments') === -1 && window.location?.pathname?.indexOf('new') === -1;

  // Click handlers
  const setTriggerGuidedTour = () => {
    localStorage.setItem(SHOW_HOME_GUIDED_TOUR_KEY, true);
    EventEmitter.dispatch(EventEmitterTypes.SHOW_HOME_GUIDED_TOUR, { state: true });
  };
  const goToCompanyRequests = e => {
    e.preventDefault();
    navDrawerOnChange();
    history.push(Routes.companyRequests.path);
  };
  const goToCustomerRequests = e => {
    e.preventDefault();
    navDrawerOnChange();
    history.push(Routes.customerRequests.path);
  };
  const showUserProfile = e => {
    e.preventDefault();
    navDrawerOnChange();
    toggleUserProfileModal();
  };
  const showHelpModal = e => {
    e.preventDefault();
    navDrawerOnChange();
    displayHelpModal();
  };
  const showCreateNewModal = e => {
    e.preventDefault();
    navDrawerOnChange();
    displayCreateNewModal();
  };
  const triggerGuidedTour = e => {
    e.preventDefault();
    navDrawerOnChange();
    setTriggerGuidedTour();
  };
  const goToSignOut = e => {
    e.preventDefault();
    history.push(Routes.logout.path);
  };

  const customerAssessments = assessments.filter(assessment => {
    return !assessment.productIdentifier || assessment.productIdentifier === 'E4V';
  });
  const companyAssessments = assessments.filter(assessment => {
    return assessment.productIdentifier === 'E4BU';
  });
  const hasAssessments = customerAssessments.length || companyAssessments.length;

  const getHomePageLinks = () => {
    const homePageLinks = [];
    if (customerAssessments.length) {
      homePageLinks.push({
        linkText: translate('customerRequests.title'),
        id: 'navBar-customerRequests',
        clickAction: e => goToCustomerRequests(e)
      });
    }
    if (companyAssessments.length) {
      homePageLinks.push({
        linkText: translate('companyRequests.title'),
        id: 'navBar-companyRequests',
        clickAction: e => goToCompanyRequests(e)
      });
    }
    return homePageLinks;
  };

  const getNavLinks = () => {
    const staticNavLinkList = [];
    const accountSectionLinkList = [
      {
        linkText: translate('navSideBar.myProfile'),
        id: 'navBar-my-profile',
        clickAction: e => showUserProfile(e)
      },
      {
        linkText: translate('common.globalHelp'),
        id: 'navBar-help',
        clickAction: e => showHelpModal(e)
      },
      {
        linkText: translate('common.logout'),
        id: 'navBar-logout',
        clickAction: e => goToSignOut(e)
      }
    ];
    if (isHomePage) {
      accountSectionLinkList.splice(2, 0, {
        linkText: translate('help.guidedTour'),
        id: 'navBar-guidedTour',
        clickAction: e => triggerGuidedTour(e)
      });
    }
    if (hasAssessments) {
      staticNavLinkList.push({
        linkSectionHeading: translate('navSideBar.myActivities'),
        linkList: getHomePageLinks()
      });
    }
    if (templateData.length) {
      staticNavLinkList.push({
        linkSectionHeading: translate('navSideBar.actions'),
        linkList: [
          {
            linkText: translate('navSideBar.createNew'),
            id: 'navBar-createNew',
            clickAction: e => showCreateNewModal(e)
          }
        ]
      });
    }
    staticNavLinkList.push({
      linkSectionHeading: translate('navSideBar.account'),
      linkList: accountSectionLinkList
    });
    return staticNavLinkList;
  };

  return (
    <React.Fragment>
      <Drawer
        classes={{ root: drawerRoot, paper: drawerPaper, docked: drawerDocked }}
        anchor="left"
        open={open}
        onClose={navDrawerOnChange}
        variant="temporary"
      >
        <nav>
          {getNavLinks().map(navLink => {
            const { linkSectionHeading, linkList } = navLink;
            const navLinkSectionProps = {
              linkSectionHeading,
              linkList
            };
            return <NavLinkSection {...navLinkSectionProps} key={linkSectionHeading} />;
          })}
        </nav>
      </Drawer>
    </React.Fragment>
  );
};

export const NavSideBarHOC = compose(
  withRouter,
  graphql(listAssessments, { options: { fetchPolicy: NETWORK_ONLY } }),
  graphql(getDomainRestrictedTemplates, { name: 'getDomainRestrictedTemplates' }),
  mapProps(props => ({
    history: props.history,
    open: props.open,
    navDrawerOnChange: props.navDrawerOnChange,
    toggleUserProfileModal: props.toggleUserProfileModal,
    displayHelpModal: props.displayHelpModal,
    displayCreateNewModal: props.displayCreateNewModal,
    assessments: getAssessments(props),
    templateData: getDomainRestrictedTemplatesSelector(props)
  }))
);

export const NavSideBar = NavSideBarHOC(NavSideBarBase);
